import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const userSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').optional(),
  first_name: z.string().min(1, 'Le prénom est requis'),
  last_name: z.string().min(1, 'Le nom est requis'),
  phone: z.string().nullable().optional(),
  role: z.enum(['guest', 'support', 'owner', 'admin', 'super_admin']),
  preferred_language: z.enum(['fr', 'en', 'it'])
});

type UserFormData = z.infer<typeof userSchema>;

interface UserFormProps {
  user?: any;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UserForm({ user, onClose, onSuccess }: UserFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useNotification();
  const { userRole } = useAuth();
  const { t } = useTranslation();

  const isEditingSuperAdmin = user?.role === 'super_admin';
  const isSuperAdmin = userRole === 'super_admin';

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: user || {
      role: 'guest',
      preferred_language: 'fr'
    }
  });

  const watchedRole = watch('role');

  const getAvailableRoles = () => {
    if (isSuperAdmin) {
      return [
        { value: 'guest', label: 'Invité' },
        { value: 'support', label: 'Support' },
        { value: 'owner', label: 'Propriétaire' },
        { value: 'admin', label: 'Administrateur' },
        { value: 'super_admin', label: 'Super Administrateur' }
      ];
    } else if (userRole === 'admin') {
      return [
        { value: 'guest', label: 'Invité' },
        { value: 'support', label: 'Support' },
        { value: 'owner', label: 'Propriétaire' },
        { value: 'admin', label: 'Administrateur' }
      ];
    }
    return [];
  };

  async function onSubmit(data: UserFormData) {
    try {
      setIsSubmitting(true);
      setError(null);

      // Vérifications de sécurité
      if (userRole !== 'super_admin' && userRole !== 'admin') {
        throw new Error('Permissions insuffisantes');
      }

      if (userRole === 'admin') {
        if (data.role === 'super_admin') {
          throw new Error('Les administrateurs ne peuvent pas attribuer le rôle super admin');
        }
        if (isEditingSuperAdmin) {
          throw new Error('Les administrateurs ne peuvent pas modifier un super admin');
        }
      }

      if (user?.id) {
        // Mise à jour du profil existant
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: data.first_name,
            last_name: data.last_name,
            phone: data.phone || null,
            role: data.role,
            preferred_language: data.preferred_language
          })
          .eq('id', user.id)
          .not('role', 'eq', 'super_admin');

        if (profileError) {
          throw new Error('Erreur lors de la mise à jour du profil');
        }
      } else {
        // Création d'un nouvel utilisateur
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email: data.email,
          password: data.password!
        });

        if (signUpError) throw signUpError;

        if (authData.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              first_name: data.first_name,
              last_name: data.last_name,
              role: data.role,
              preferred_language: data.preferred_language,
              phone: data.phone || null
            })
            .eq('id', authData.user.id);

          if (profileError) throw profileError;
        }
      }

      addNotification('success', user?.id 
        ? 'Utilisateur modifié avec succès' 
        : 'Utilisateur créé avec succès'
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message);
      addNotification('error', err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (userRole !== 'admin' && userRole !== 'super_admin') {
    return null;
  }

  if (userRole === 'admin' && isEditingSuperAdmin) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {!user && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    {...register('password')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Prénom
              </label>
              <input
                type="text"
                {...register('first_name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nom
              </label>
              <input
                type="text"
                {...register('last_name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Téléphone
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rôle
              </label>
              <select
                {...register('role')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={userRole === 'admin' && isEditingSuperAdmin}
              >
                {getAvailableRoles().map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Langue préférée
              </label>
              <select
                {...register('preferred_language')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="it">Italiano</option>
              </select>
              {errors.preferred_language && (
                <p className="mt-1 text-sm text-red-600">{errors.preferred_language.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
              >
                {isSubmitting ? 'Enregistrement...' : user ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}